// Blazes: player-placed cairns with brass pins (trades.md §22). Matter,
// not markers: they render at real coordinates, occlude like everything
// else, and announce nothing — a cairn does not know you are looking for
// it. Fetched on scene creation and on a slow tick; a wall that cannot
// reach the server keeps yesterday's marks.

import * as THREE from "three";
import {
  type AuthoredPlacement,
  disposeAuthoredObject,
  instanceAuthoredAsset,
} from "./authored-assets";
import type { Tracker } from "./common";

const REFRESH_MS = 60_000;

interface BlazeInfo {
  x: number;
  z: number;
  scoutName: string;
}

export class BlazeLayer {
  readonly group = new THREE.Group();
  private timer: ReturnType<typeof setInterval> | undefined;
  private disposed = false;
  constructor(
    _t: Tracker,
    private groundY: (x: number, z: number) => number,
    private room: string,
  ) {
    void this.refresh();
    this.timer = setInterval(() => void this.refresh(), REFRESH_MS);
  }

  private async refresh(): Promise<void> {
    try {
      const res = await fetch(`/api/blazes?room=${encodeURIComponent(this.room)}`);
      if (!res.ok || this.disposed) return;
      const { blazes } = (await res.json()) as { blazes: BlazeInfo[] };
      if (this.disposed) return;
      const stones: AuthoredPlacement[] = [];
      const pins: AuthoredPlacement[] = [];
      for (const blaze of blazes) {
        const y = this.groundY(blaze.x, blaze.z);
        // Four stones make a cairn with gaps and a varied silhouette. All
        // blazes share one instanced authored rock; positions still come
        // exclusively from the persisted scout marks.
        for (const [dx, dy, dz, width, height, depth, yaw] of [
          [0, 0, 0, 1.02, 0.35, 0.94, 0.2],
          [-0.05, 0.28, 0.03, 0.81, 0.38, 0.74, 1.1],
          [0.04, 0.6, -0.03, 0.58, 0.35, 0.54, 2.2],
          [0, 0.89, 0, 0.32, 0.34, 0.31, 0.7],
        ] as const) {
          stones.push({ x: blaze.x + dx, y: y + dy, z: blaze.z + dz, width, height, depth, yaw });
        }
        pins.push({ x: blaze.x, y: y + 1.2, z: blaze.z, width: 0.18, height: 0.19, depth: 0.18 });
      }
      const next = new THREE.Group();
      try {
        const cairns = instanceAuthoredAsset("nature-rock", stones);
        // Nature's source geometry is neutral so each biome can tint it.
        // Scout marks retain their established dark local-stone colour.
        cairns.traverse((object) => {
          if (!(object instanceof THREE.Mesh)) return;
          const materials = Array.isArray(object.material) ? object.material : [object.material];
          for (const material of materials) {
            if (material instanceof THREE.MeshStandardMaterial) material.color.set(0x56504a);
          }
        });
        next.add(cairns);
        next.add(instanceAuthoredAsset("frontier-blaze-pin", pins));
      } catch (error) {
        disposeAuthoredObject(next);
        throw error;
      }
      // Swap only after both batches exist. Failed refreshes retain the
      // previous marks and dispose no catalog-owned geometry or textures.
      disposeAuthoredObject(this.group);
      this.group.clear();
      this.group.add(next);
    } catch {
      // Keep yesterday's marks.
    }
  }

  dispose(): void {
    this.disposed = true;
    if (this.timer) clearInterval(this.timer);
    disposeAuthoredObject(this.group);
    this.group.clear();
  }
}
